// The earnings model on /specialists: a short example in the hero and the full
// calculator further down, reading the same numbers. A specialist runs rollouts with
// Eric and can charge for that work, or hands them to Eric and earns the share alone.
// BossDrop's prices stay off the website, so the monthly figure is a number to try, and
// it starts on a round one that reads as arithmetic. Every dollar on the public page
// lives in here. Nothing comes from another specialist's results.
import React, { useEffect, useRef, useState } from "react";

// Eric's call. The page, the FAQs and the model all read the share from here.
export const SHARE_PCT = 20;
export const SHARE = `${SHARE_PCT}%`;
export const SHARE_MONTHS = 12;
// With the pilot first, a share starts about three months after the introduction.
export const FIRST_SHARE_MONTH = 4;

export const DEFAULT_INPUTS = Object.freeze({ clients: 3, fee: 5000, monthly: 1000, rollout: "together" });

const CLIENT_CHOICES = [1, 3, 5, 10].map((n) => ({
  value: n,
  label: String(n),
  name: `${n} ${n === 1 ? "client" : "clients"}`,
}));
const ROLLOUT_CHOICES = [
  { value: "together", label: "You and Eric", name: "You and Eric run the rollout" },
  { value: "eric", label: "Just Eric", name: "Eric runs the rollout" },
];

export const NOT_A_PRICE = "a number to try, not a price";

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const usd = (dollars) => USD.format(Math.round(dollars));
const clientWord = (n) => (n === 1 ? "client" : "clients");
const someClients = (n) => `${n} ${clientWord(n)}`;

// Saying who pays is what keeps "your fees" from reading as something you owe.
const feeLabel = (clients) => (clients === 1 ? "Your fee, paid by your client" : "Your fees, paid by your clients");
const SHARE_LABEL = `Your ${SHARE}, paid by BossDrop`;

const feeEach = ({ fee, rollout }) => (rollout === "eric" ? 0 : fee);

export function modelNumbers(inputs) {
  const { clients, monthly } = inputs;
  const sharePerClient = (monthly * SHARE_MONTHS * SHARE_PCT) / 100;
  const fees = clients * feeEach(inputs);
  const share = clients * sharePerClient;
  return { fees, share, sharePerClient, perClient: feeEach(inputs) + sharePerClient, total: fees + share };
}

// What the chart draws: the running total if every client starts in the same month.
export function runningTotals(inputs) {
  const { clients, monthly } = inputs;
  const fees = clients * feeEach(inputs);
  const shareEachMonth = (clients * monthly * SHARE_PCT) / 100;
  return Array.from({ length: FIRST_SHARE_MONTH - 1 + SHARE_MONTHS }, (_, i) => {
    const month = i + 1;
    const share = Math.max(0, month - FIRST_SHARE_MONTH + 1) * shareEachMonth;
    return { month, fees, share, total: fees + share };
  });
}

const holdStill = () =>
  !window.matchMedia || window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// A number that runs to its new value instead of jumping. With `countUp`, it starts at zero.
function useTween(target, countUp = false) {
  const [shown, setShown] = useState(() => (countUp && !holdStill() ? 0 : target));
  const latest = useRef(shown);
  const firstRun = useRef(true);

  useEffect(() => {
    const ms = firstRun.current ? 900 : 320;
    firstRun.current = false;
    const begin = latest.current;
    if (begin === target) return undefined;
    if (holdStill()) {
      latest.current = target;
      setShown(target);
      return undefined;
    }
    const started = performance.now();
    let frame = requestAnimationFrame(function tick(now) {
      const t = Math.min(1, Math.max(0, (now - started) / ms));
      const value = begin + (target - begin) * (1 - (1 - t) ** 3);
      latest.current = value;
      setShown(value);
      if (t < 1) frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [target]);

  return shown;
}

function Money({ value, countUp }) {
  return usd(useTween(value, countUp));
}

function Choices({ label, options, value, onChange }) {
  return (
    <div className="smChoices" role="group" aria-label={label} style={{ "--n": options.length }}>
      {options.map((option) => (
        <button
          type="button"
          key={option.value}
          className="smChoice"
          aria-pressed={option.value === value ? "true" : "false"}
          aria-label={option.name}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/* ---- The card in the hero ---- */

export function HeroExample({ inputs, onChange, onChangeNumbers }) {
  const { clients, fee, monthly, rollout } = inputs;
  const { fees, share, total } = modelNumbers(inputs);
  return (
    <aside className="smCard" data-model aria-label="An example of what a specialist could earn">
      <div className="smCardKicker">An example, not a promise</div>
      <h2 className="smCardTitle">What {someClients(clients)} could look like</h2>
      <div className="smCardControls">
        <div>
          <span>Clients</span>
          <Choices
            label="Clients you bring"
            options={CLIENT_CHOICES}
            value={clients}
            onChange={(n) => onChange({ clients: n })}
          />
        </div>
        <div>
          <span>Who runs the rollout</span>
          <Choices
            label="Who runs the rollout"
            options={ROLLOUT_CHOICES}
            value={rollout}
            onChange={(who) => onChange({ rollout: who })}
          />
        </div>
      </div>
      <dl className="smCardRows">
        {rollout === "together" ? (
          <div>
            <dt>
              {feeLabel(clients)}
              <small>
                {someClients(clients)} × {usd(fee)}
              </small>
            </dt>
            <dd>
              <Money value={fees} countUp />
            </dd>
          </div>
        ) : null}
        <div>
          <dt>
            {SHARE_LABEL}
            <small>
              {someClients(clients)} × {usd(monthly)} a month × {SHARE_MONTHS} months × {SHARE}
            </small>
          </dt>
          <dd>
            <Money value={share} countUp />
          </dd>
        </div>
      </dl>
      {rollout === "eric" ? <p className="smCardNote">Eric does the work. You make the introduction.</p> : null}
      <div className="smCardTotal">
        <span>
          Estimated total
          <small>Before your costs and taxes. Results vary.</small>
        </span>
        <strong>
          <Money value={total} countUp />
        </strong>
      </div>
      <p className="smCardFoot">
        {usd(monthly)} a month is {NOT_A_PRICE}.{" "}
        <a href="#the-math" onClick={onChangeNumbers}>
          Change the numbers
        </a>
      </p>
    </aside>
  );
}

/* ---- The calculator ---- */

function Slider({ id, label, value, min, max, step, format, hint, onChange }) {
  return (
    <div className="smField">
      <div className="smFieldTop">
        <label htmlFor={id}>{label}</label>
        <span className="smFieldValue">{format(value)}</span>
      </div>
      <input
        id={id}
        className="smSlider"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-valuetext={format(value)}
        style={{ "--pct": `${((value - min) / (max - min)) * 100}%` }}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <p className="smHint">{hint}</p>
    </div>
  );
}

function RunningChart({ inputs }) {
  const rows = runningTotals(inputs);
  const last = rows[rows.length - 1];
  const peak = Math.max(1, last.total);
  const [picked, setPicked] = useState(null);
  const shown = rows[(picked || last.month) - 1];
  const height = (part) => `${(part / peak) * 100}%`;
  const hasFees = last.fees > 0;

  return (
    <figure className="smChart">
      <figcaption className="smChartHead">
        <span>Running total, month by month</span>
        <span className="smChartRead">
          <strong>{usd(shown.total)}</strong> by month {shown.month}
        </span>
      </figcaption>
      <div
        className="smBars"
        role="img"
        aria-label={`${hasFees ? `Fees of ${usd(last.fees)} come first. ` : ""}The ${SHARE} starts in month ${FIRST_SHARE_MONTH} and takes the running total to ${usd(last.total)} by month ${last.month}.`}
        onPointerLeave={(e) => {
          if (e.pointerType === "mouse") setPicked(null);
        }}
      >
        {rows.map((row) => (
          <div
            key={row.month}
            className={`smBarSlot${picked === row.month ? " isPicked" : ""}`}
            style={{ "--i": row.month }}
            onPointerEnter={() => setPicked(row.month)}
            onPointerDown={() => setPicked(row.month)}
          >
            {row.share > 0 ? <span className="smBar smBarShare" style={{ height: height(row.share) }} /> : null}
            {row.fees > 0 ? <span className="smBar smBarFees" style={{ height: height(row.fees) }} /> : null}
          </div>
        ))}
      </div>
      <div className="smAxis">
        <span style={{ flexGrow: FIRST_SHARE_MONTH - 1 }}>The pilot</span>
        <span style={{ flexGrow: SHARE_MONTHS }}>
          Your {SHARE}: {SHARE_MONTHS} months from their first payment
        </span>
      </div>
      {/* One kind of bar needs no key: the chart's title and axis already say what it is. */}
      {hasFees ? (
        <ul className="smLegend">
          <li>
            <i className="smKey smKeyFees" /> Your fees
          </li>
          <li>
            <i className="smKey smKeyShare" /> Your {SHARE}
          </li>
        </ul>
      ) : null}
      <details className="smTableWrap">
        <summary>See it as a table</summary>
        <table className="smTable">
          <thead>
            <tr>
              <th scope="col">Month</th>
              <th scope="col">Your fees</th>
              <th scope="col">Your {SHARE} so far</th>
              <th scope="col">Running total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.month}>
                <td>{row.month}</td>
                <td>{usd(row.fees)}</td>
                <td>{usd(row.share)}</td>
                <td>{usd(row.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}

export function EarningsModel({ inputs, onChange }) {
  const { clients, fee, monthly, rollout } = inputs;
  const { fees, share, perClient, total } = modelNumbers(inputs);
  return (
    <div className="smModel reveal" data-reveal data-model>
      <div className="smInputs">
        <div className="smField">
          <div className="smFieldTop">
            <span className="smFieldLabel">Who runs the rollout?</span>
          </div>
          <Choices
            label="Who runs the rollout"
            options={ROLLOUT_CHOICES}
            value={rollout}
            onChange={(who) => onChange({ rollout: who })}
          />
          <p className="smHint">
            {rollout === "eric"
              ? "Eric does the work, and you earn the share."
              : "You work with Eric, charge your client for your work and keep it all."}
          </p>
        </div>
        <Slider
          id="sm-clients"
          label="Clients you bring"
          value={clients}
          min={1}
          max={10}
          step={1}
          format={someClients}
          hint="Each one a new company for BossDrop, starting with one team."
          onChange={(n) => onChange({ clients: n })}
        />
        {rollout === "together" ? (
          <Slider
            id="sm-fee"
            label="What you charge each client"
            value={fee}
            min={0}
            max={25000}
            step={500}
            format={usd}
            hint="Yours to set and yours to keep. Try what 45 days of your time is worth."
            onChange={(n) => onChange({ fee: n })}
          />
        ) : null}
        <Slider
          id="sm-monthly"
          label="What each client pays BossDrop a month"
          value={monthly}
          min={500}
          max={10000}
          step={500}
          format={usd}
          hint={`This is ${NOT_A_PRICE}. BossDrop quotes each team itself, and your terms explain how pricing works.`}
          onChange={(n) => onChange({ monthly: n })}
        />
      </div>

      <div className="smOutput">
        <div className="smTotalLabel">Estimated total</div>
        <div className="smTotal">
          <Money value={total} />
        </div>
        <p className="smPer">
          {usd(perClient)} per client × {someClients(clients)}
        </p>
        <p className="sr-only" aria-live="polite">
          Estimated total {usd(total)}: {rollout === "together" ? `${usd(fees)} in your fees and ` : ""}
          {usd(share)} in your {SHARE}.
        </p>
        <dl className="smRows">
          {rollout === "together" ? (
            <div>
              <dt>
                <i className="smKey smKeyFees" /> {feeLabel(clients)}
                <small>
                  {someClients(clients)} × {usd(fee)}
                </small>
              </dt>
              <dd>
                <Money value={fees} />
              </dd>
            </div>
          ) : null}
          <div>
            <dt>
              <i className="smKey smKeyShare" /> {SHARE_LABEL}
              <small>
                {someClients(clients)} × {usd(monthly)} a month × {SHARE_MONTHS} months × {SHARE}
              </small>
            </dt>
            <dd>
              <Money value={share} />
            </dd>
          </div>
        </dl>
        <RunningChart inputs={inputs} />
      </div>
    </div>
  );
}
